import * as React from "react";
import { useEffect } from "react";
import styled, { css } from "styled-components";
import { useTranslation } from "react-i18next";
import i18n from "i18n";
import { Markdown, Spinner } from "components/common";
import { Alert } from "components/bootstrap";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import { REPORT_SECTION_ID } from "./ColumnRenderers";
import TraceSection from "./TraceSection";
import useWorkflowRun from "./useWorkflowRun";
const ReportBody = styled.div(
  ({ theme }) => css`
    padding: ${theme.spacings.sm} ${theme.spacings.md};
    background: ${theme.colors.global.background};
    border: 1px solid ${theme.colors.cards.border};
    border-radius: ${theme.spacings.xxs};

    /* No height cap or inner scroll — the box grows to fit the full markdown report. */

    p:first-child,
    h1:first-child,
    h2:first-child,
    h3:first-child {
      margin-top: 0;
    }
  `
);
const ReportSection = ({ entry }) => {
  const { t } = useTranslation("enterprise_ai");
  const sendTelemetry = useSendTelemetry();
  const { data, isLoading, isError, error } = useWorkflowRun(entry.id);
  useEffect(() => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.AI_AGENT_WORKFLOW.WORKFLOW_RUN_DETAILS_OPENED, {
      app_section: "aiagent-workflows",
      event_details: { workflow_type: entry.type }
    });
  }, [entry.type, sendTelemetry]);
  if (isLoading) {
    return /* @__PURE__ */ React.createElement(Spinner, { text: t("expanded_sections.loading_report") });
  }
  if (isError) {
    return /* @__PURE__ */ React.createElement(Alert, { bsStyle: "danger", title: t("expanded_sections.load_failed") }, error instanceof Error ? error.message : t("common_texts.unknown_error"));
  }
  return /* @__PURE__ */ React.createElement(React.Fragment, null, (data == null ? void 0 : data.state) === "running" && /* @__PURE__ */ React.createElement(Alert, { bsStyle: "info" }, /* @__PURE__ */ React.createElement(Spinner, { text: t("common_texts.agent_working") })), (data == null ? void 0 : data.state) === "failed" && data.error && /* @__PURE__ */ React.createElement(Alert, { bsStyle: "danger", title: t("common_texts.did_not_complete") }, data.error), (data == null ? void 0 : data.final_message) && /* @__PURE__ */ React.createElement(ReportBody, null, /* @__PURE__ */ React.createElement(Markdown, { augment: true, text: data.final_message })), data && /* @__PURE__ */ React.createElement(TraceSection, { entry: data }));
};
const expandedSections = {
  [REPORT_SECTION_ID]: {
    // A getter, so the title is translated when the table renders it, not when this module loads.
    get title() {
      return i18n.t("expanded_sections.report_title", { ns: "enterprise_ai" });
    },
    // Rendered as an element, not called as a function: `content` is invoked during the parent's
    // render, so hooks in here need a component instance of their own.
    content: (entry) => /* @__PURE__ */ React.createElement(ReportSection, { entry })
  }
};
export default expandedSections;
